import { ChevronRightIcon, Link2Off, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { RiskReductionMeasureLinkedHazard } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'

function getHazardTypeLabel(
  type: string | null,
  naLabel: string,
  translate: (key: string) => string
) {
  if (!type) {
    return naLabel
  }
  return translate(`hazardsPage.enums.hazardTypes.${type}`) || type
}

type RiskReductionMeasureLinkedHazardsSectionProps = {
  linkedHazards: RiskReductionMeasureLinkedHazard[]
  unlinkingHazardId: string | null
  onUnlinkClick: (hazardId: string) => void
}

export function RiskReductionMeasureLinkedHazardsSection({
  linkedHazards,
  unlinkingHazardId,
  onUnlinkClick,
}: RiskReductionMeasureLinkedHazardsSectionProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const naLabel = t('hazardsPage.hazardDetail.na')
  const hasLinkedHazards = linkedHazards.length > 0

  return (
    <section className='space-y-3'>
      <h2 className='text-lg font-semibold tracking-tight'>
        {t('riskReductionMeasuresLibraryPage.rrmDetail.linkedHazardsTitle')}
      </h2>
      {!hasLinkedHazards && (
        <p className='text-sm text-muted-foreground'>
          {t('riskReductionMeasuresLibraryPage.list.linkedHazardsEmpty')}
        </p>
      )}
      {hasLinkedHazards && (
        <ul className='space-y-1'>
          {linkedHazards.map((hazard) => {
            const hazardLabel = getHazardTypeLabel(hazard.type, naLabel, t)
            const isUnlinking = unlinkingHazardId === hazard.id
            const hasDescription =
              hazard.description != null && hazard.description.trim().length > 0

            return (
              <li key={hazard.id}>
                <Item variant='outline' size='sm'>
                  <ItemContent className='min-w-0 flex-1'>
                    <ItemTitle className='cursor-default text-foreground'>
                      {hazardLabel}
                    </ItemTitle>
                    {hasDescription ? (
                      <ItemDescription className='line-clamp-2 text-xs text-muted-foreground'>
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <span className='block cursor-default'>
                              {hazard.description}
                            </span>
                          </HoverCardTrigger>
                          <HoverCardContent
                            side='top'
                            className='max-w-sm text-sm text-muted-foreground'
                          >
                            <p className='whitespace-pre-wrap'>
                              {hazard.description}
                            </p>
                          </HoverCardContent>
                        </HoverCard>
                      </ItemDescription>
                    ) : null}
                  </ItemContent>
                  <div className='flex shrink-0 items-center gap-1'>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='shrink-0 text-muted-foreground hover:text-destructive'
                      onClick={() => onUnlinkClick(hazard.id)}
                      disabled={isUnlinking}
                      aria-label={t(
                        'riskReductionMeasuresLibraryPage.rrmDetail.unlinkFromHazardAria',
                        { hazard: hazardLabel }
                      )}
                    >
                      {isUnlinking ? (
                        <Loader2 className='size-4 animate-spin' />
                      ) : (
                        <>
                          <Link2Off className='size-4' />
                          {t(
                            'riskReductionMeasuresLibraryPage.rrmDetail.unlinkAction'
                          )}
                        </>
                      )}
                    </Button>
                    <Link
                      relative='route'
                      to={`../hazards/${hazard.id}`}
                      className='inline-flex size-8 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground'
                      aria-label={hazardLabel}
                    >
                      <ChevronRightIcon className='size-4' />
                    </Link>
                  </div>
                </Item>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
